import { Observer, observer } from 'mobx-react-lite'
import { bindInput } from '@/infra/view-binding'
import type { FilterProps } from '@/infra/type-utils'
import { TextArea, type TextAreaProps } from '@/components/inputs/TextArea'
import {
  type ContainerProps,
  FormFieldContainer,
} from '@/components/FormContainer'
import { isNotNil } from '@/utils/object-util'

interface TextAreaContainerControlProps extends Omit<
  TextAreaProps,
  'value' | 'onChange'
> {
  focusOnError?: boolean
}

/**
 * Form field container for a {@link TextArea}.
 */
export const TextAreaContainer = observer(function TextAreaContainer<
  M extends object,
  K extends keyof FilterProps<M, string>,
>({
  name,
  model,
  label,
  sublabel,
  help,
  field,
  error,
  focusOnError = false,
  ...rest
}: ContainerProps<M, string, K, TextAreaContainerControlProps>) {
  return (
    <FormFieldContainer
      name={name}
      model={model}
      label={label}
      sublabel={sublabel}
      help={help}
      field={field}
      error={error}
      render={(controlProps) => (
        // Using `Observer` here makes it so only the `TextArea`
        // re-renders when the value changes.
        <Observer>
          {() => {
            const { value, onChange } = bindInput(model, field)
            return (
              <TextArea
                value={isNotNil(value) ? value : ''}
                onChange={(e) => onChange(e.target.value)}
                focusedError={focusOnError ? error : undefined}
                status={controlProps.invalid ? 'danger' : 'default'}
                name={controlProps.name}
                {...rest}
              />
            )
          }}
        </Observer>
      )}
    />
  )
})
